"use client";

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useMemo,
  useState,
} from "react";
import { useRouter, usePathname } from "next/navigation";
import { useSession } from "@/db/auth/client";
import type { AuthSession, Session } from "@/os/types/db/auth/session";
import { getReturnUrl, phoneGateReturnPath } from "@/lib/auth/redirect";
import { internalAuthPath } from "@garagem/foundation-shared/lib/auth/safe-return-path";
import { hasStableSessionChanged } from "@web/lib/auth/stable-session-change";
import {
  shouldBounceUnverifiedPhone,
  shouldBounceUnverifiedToConfirmation,
  shouldDeferEmailConfirmationForPhone,
  shouldDeferTermsAcceptanceForPhone,
} from "@web/lib/auth/unverified-bounce-policy";
import { trpc } from "@/lib/trpc/client";
import {
  TERMS_GATE_HOLD_MS,
  isTermsGateHoldingEntrar,
} from "@/components/providers/auth-terms-gate";

/**
 * Shape of the value exposed through the auth context. Two structurally
 * different sources feed it: the Better Auth client session (revalidated in the
 * background) and the enriched SSR `AuthSession`. Fields are optional and use
 * the most permissive (nullable) shape common to both sources, and the index
 * signatures keep the many `useAuth()` consumers that read enriched fields
 * working without resorting to `any`.
 */
export interface AuthSessionUser {
  id: string;
  email: string;
  name?: string | null;
  image?: string | null;
  emailVerified?: boolean;
  phoneNumberVerified?: boolean | null;
  banned?: boolean | null;
  role?: string | null;
  roleId?: string | null;
  permissions?: string[];
  personalTenantId?: string | null;
  twoFactorEnabled?: boolean | null;
  createdAt?: string | Date | null;
  updatedAt?: string | Date | null;
  // Profile fields carried on the enriched session user (consumed e.g. by the
  // profile form). Optional/nullable to match the SSR `AuthSession["user"]`.
  phone?: string | null;
  cpf?: string | null;
  rg?: string | null;
  rgIssuer?: string | null;
  creci?: string | null;
  creciType?: string | null;
  creciExpiryDate?: string | Date | null;
  bankData?: unknown;
  [key: string]: unknown;
}

export interface AuthSessionInner {
  id?: string;
  activeOrganizationId?: string | null;
  activeTenantId?: string | null;
  activeTeamId?: string | null;
  impersonatedBy?: string | null;
  tenantImpersonatedBy?: string | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  // Some legacy consumers defensively read `session.session.member` (the
  // enriched member is sometimes nested under the inner session object).
  member?: Session["member"];
  [key: string]: unknown;
}

export interface AuthSessionState {
  // A materialized session always carries a user; keeping this non-optional
  // lets consumers read `session.user.*` after a `session` guard without the
  // narrowing being lost inside nested callbacks.
  user: AuthSessionUser;
  session?: AuthSessionInner | null;
  member?: AuthSession["member"];
  activeOrganization?: AuthSession["activeOrganization"];
  hasPendingTerms?: boolean;
  isSuspended?: boolean;
  suspendedUntil?: Date | string | null;
  suspensionReason?: string | null;
  /** Active organization is lifecycle-blocked; the session grants nothing. */
  organizationLifecycle?: AuthSession["organizationLifecycle"];
  roleId?: string | null;
  rolePermissions?: Record<string, unknown> | null;
  entityType?: string | null;
  [key: string]: unknown;
}

export interface AuthContextType {
  isLoading: boolean;
  isAuthenticated: boolean;
  session: AuthSessionState | null;
  user: AuthSessionState["user"] | null;
}

const AuthContext = createContext<AuthContextType>({
  isLoading: true,
  isAuthenticated: false,
  session: null,
  user: null,
});

export function useAuth() {
  return useContext(AuthContext);
}

const publicRoutes = [
  "/entrar",
  "/cadastrar",
  "/esqueci-senha",
  "/redefinir-senha",
  "/verificar-email",
  "/aceitar-convite",
  "/aguardando-confirmacao",
  "/verificar-telefone",
  "/verificar",
  "/acesso-bloqueado",
  "/aceitar-termos",
  "/instalar",
];

function isPortalSubdomain(): boolean {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname;
  return hostname.startsWith("portal.");
}

/**
 * Stabilize the session reference: only return a new object when meaningful
 * fields actually change, preventing cascading re-renders from better-auth's
 * nanostores atom returning new references on every /get-session response.
 */
function useStableSession(rawSession: AuthSessionState | null) {
  const stableRef = useRef(rawSession);

  if (hasStableSessionChanged(stableRef.current, rawSession)) {
    stableRef.current = rawSession;
  }

  return stableRef.current;
}

export function AuthProvider({
  children,
  initialSession = null,
}: {
  children: React.ReactNode;
  /**
   * Server-computed, enriched session injected by the root layout. When
   * present the app renders immediately with it (no blocking `get-session`);
   * `useSession()` still revalidates in the background and takes over once it
   * resolves (so logout / org-switch stay correct).
   */
  initialSession?: AuthSessionState | null;
}) {
  const { data: rawSession, isPending } = useSession();
  // While Better Auth is still resolving, fall back to the SSR session.
  // Once resolved, `rawSession` is authoritative (even when null = logged out).
  const effectiveRaw = isPending ? (initialSession ?? rawSession) : rawSession;
  const session = useStableSession(effectiveRaw);
  const router = useRouter();
  const pathname = usePathname();
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!initialSession?.user
  );

  // Auth is "resolved enough" to act on once Better Auth settles OR we already
  // have an SSR session in hand.
  const authResolved = !isPending || !!initialSession;

  const isPortal = isPortalSubdomain();
  const isPublicRoute =
    isPortal ||
    (process.env.NODE_ENV === "development" &&
      pathname?.startsWith("/ui-composition-lab")) ||
    publicRoutes.some((route) => pathname?.startsWith(route));
  // Control-plane only: a brand-new org's database is still being created
  // when the first session lands (gar-467.23.94). Poll until ready so we do
  // not open the tenant store for terms (or any other tenant work) yet.
  const workspaceReadinessEnabled =
    authResolved &&
    !!session?.user &&
    !!session.session?.activeOrganizationId &&
    !isPortal;
  const { data: workspaceReadiness } = trpc.auth.getWorkspaceReadiness.useQuery(
    undefined,
    {
      enabled: workspaceReadinessEnabled,
      staleTime: 0,
      refetchInterval: (query) =>
        query.state.data?.readiness === "preparing" ? 3_000 : false,
      refetchOnWindowFocus: true,
    }
  );
  // Pending-term synchronization reads and may create tenant rows. Keep that
  // work on this explicit tenant query, after the Better Auth session has
  // resolved and the workspace is ready to open, rather than hiding it
  // inside `/get-session` enrichment.
  // A lifecycle-blocked organization (suspended, expired, …) has no tenant
  // database to open: the query could only answer the lifecycle refusal.
  const termsQueryEnabled =
    workspaceReadinessEnabled &&
    !session?.organizationLifecycle &&
    pathname !== "/aceitar-termos" &&
    workspaceReadiness?.readiness === "ready";
  const { data: pendingAcceptances, isPending: isPendingTerms } =
    trpc.memberTerms.getPendingAcceptances.useQuery(undefined, {
      enabled: termsQueryEnabled,
      // Transient failures must not permanently skip the terms gate for the
      // rest of the SPA session.
      retry: 2,
      // The session hook used to run this sync at most once per user every
      // five minutes. Keep that cadence here: without it the query refetches
      // on every mount and window focus, and each run opens the tenant
      // database and may insert pending rows.
      staleTime: 5 * 60 * 1000,
      // Keep the five-minute success cadence, but recover from exhausted retries
      // when the user returns to the tab so the terms gate is not permanently
      // skipped for the rest of the SPA session.
      refetchOnWindowFocus: (query) => query.state.status === "error",
    });
  // Cap how long `/entrar` waits on the first in-flight fetch. Errors and
  // background refetches must not block login — signing in cannot depend on
  // the tenant database (suspended / pending-deletion orgs, unavailable DB).
  const [termsHoldExpired, setTermsHoldExpired] = useState(false);
  useEffect(() => {
    if (!termsQueryEnabled || !isPendingTerms) return;
    setTermsHoldExpired(false);
    const id = window.setTimeout(
      () => setTermsHoldExpired(true),
      TERMS_GATE_HOLD_MS
    );
    return () => window.clearTimeout(id);
  }, [termsQueryEnabled, isPendingTerms]);
  const termsGateUnresolved = isTermsGateHoldingEntrar({
    termsQueryEnabled,
    isPending: isPendingTerms,
    holdExpired: termsHoldExpired,
  });

  useEffect(() => {
    if (!authResolved) return;

    setIsAuthenticated(!!session?.user);

    if (!session?.user && !isPublicRoute) {
      const params = new URLSearchParams(
        typeof window !== "undefined" ? window.location.search : ""
      );
      const passthroughKeys = [
        "plan",
        "capabilities",
        "billing",
        "channels",
        "messagingChannels",
        "source",
        "campaign",
      ];
      const next = new URLSearchParams();
      next.set("from", pathname || "/");
      for (const key of passthroughKeys) {
        const v = params.get(key);
        if (v) next.set(key, v);
      }
      router.push(`/entrar?${next.toString()}`);
    } else if (
      session?.user &&
      shouldBounceUnverifiedPhone(
        pathname,
        session.user.phoneNumberVerified
      )
    ) {
      const phoneUrl = new URL("/verificar-telefone", window.location.origin);
      if (session.user.email) {
        phoneUrl.searchParams.set("email", session.user.email);
      }
      const resume = phoneGateReturnPath(
        pathname,
        new URLSearchParams(window.location.search),
      );
      if (resume) phoneUrl.searchParams.set("returnUrl", resume);
      router.push(internalAuthPath(phoneUrl));
    } else if (
      session?.user &&
      !shouldDeferEmailConfirmationForPhone(session.user.phoneNumberVerified) &&
      shouldBounceUnverifiedToConfirmation(pathname, session.user.emailVerified)
    ) {
      const confirmationUrl = new URL(
        "/aguardando-confirmacao",
        window.location.origin
      );
      confirmationUrl.searchParams.set("email", session.user.email);
      const resume = phoneGateReturnPath(
        pathname,
        new URLSearchParams(window.location.search),
      );
      if (resume) confirmationUrl.searchParams.set("returnUrl", resume);
      router.push(internalAuthPath(confirmationUrl));
    } else if (
      session?.user &&
      !shouldDeferTermsAcceptanceForPhone(session.user.phoneNumberVerified) &&
      pendingAcceptances &&
      pendingAcceptances.length > 0 &&
      pathname !== "/aceitar-termos"
    ) {
      router.push("/aceitar-termos");
    } else if (session?.user && pathname === "/entrar") {
      if (termsGateUnresolved) return;
      const searchParams = new URLSearchParams(window.location.search);
      // SECURITY: `from` / `returnUrl` / `returnTo` are user-controlled.
      // getReturnUrl only allows same-origin paths or the trusted web-return bridge.
      const returnUrl = getReturnUrl(searchParams);
      const timeoutId = setTimeout(() => {
        // Full navigation: bridge path is an API route (not a client page).
        window.location.href = returnUrl;
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [
    session,
    authResolved,
    pathname,
    router,
    isPublicRoute,
    pendingAcceptances,
    termsGateUnresolved,
  ]);

  const contextValue = useMemo<AuthContextType>(
    () => ({
      // With an SSR session we're never in a blocking-loading state on cold load.
      isLoading: isPending && !initialSession,
      isAuthenticated,
      session,
      user: session?.user || null,
    }),
    [isPending, initialSession, isAuthenticated, session]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}
